import jsPDF from 'jspdf';
import { TestResults, UserDetails } from '../../../types';
import { PDF_COLORS, getStatusColor } from '../config/colors';
import { PDF_FONTS, PDF_LAYOUT } from '../config/fonts';
import { PageManager } from '../helpers/pageBreaks';

export class CoverPageComponent {
  private pdf: jsPDF;
  private pageManager: PageManager;

  constructor(pdf: jsPDF, pageManager: PageManager) {
    this.pdf = pdf;
    this.pageManager = pageManager;
  }

  generate(results: TestResults, userDetails: UserDetails | null): void {
    this.addHeader();
    this.addUserInfo(userDetails);
    this.addScoreHighlight(results);
    this.addAssessmentSummary(results);
    this.addDisclaimer();
  }

  private addHeader(): void {
    const pageWidth = this.pageManager.getPageWidth();
    
    // Background gradient effect (simulated with rectangles)
    this.pdf.setFillColor(...PDF_COLORS.background.primary);
    this.pdf.rect(0, 0, pageWidth, 80, 'F');
    
    // Main title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.title);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'EXPORT READINESS',
      pageWidth / 2,
      40,
      { align: 'center' }
    );
    
    this.pdf.text(
      'ASSESSMENT REPORT',
      pageWidth / 2,
      60,
      { align: 'center' }
    );
    
    // Subtitle
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    this.pdf.text(
      'Comprehensive Business Evaluation for International Trade',
      pageWidth / 2,
      85,
      { align: 'center' }
    );
    
    // LSEM Branding
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.secondary);
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.text(
      'Powered by Lagos School of Export Management',
      pageWidth / 2,
      105,
      { align: 'center' }
    );
    
    this.pageManager.setCurrentY(130);
  }

  private addUserInfo(userDetails: UserDetails | null): void {
    if (!userDetails) return;
    
    const pageWidth = this.pageManager.getPageWidth();
    const contentWidth = this.pageManager.getContentWidth();
    
    // User info box
    this.pdf.setFillColor(...PDF_COLORS.background.white);
    this.pdf.setDrawColor(...PDF_COLORS.primary);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      contentWidth,
      80,
      'FD'
    );
    
    let currentY = this.pageManager.getCurrentY() + 15;
    
    // Section title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Assessment Completed By:',
      pageWidth / 2,
      currentY,
      { align: 'center' }
    );
    
    currentY += 20;
    
    // User details
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.heading);
    this.pdf.setTextColor(...PDF_COLORS.text.primary);
    this.pdf.text(
      userDetails.fullName,
      pageWidth / 2,
      currentY,
      { align: 'center' }
    );
    
    currentY += 15;
    
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    
    const userInfo = [
      `${userDetails.role}`,
      `${userDetails.businessType}`,
      `${userDetails.state}, ${userDetails.country}`,
      `${userDetails.email}`
    ];
    
    userInfo.forEach(info => {
      this.pdf.text(info, pageWidth / 2, currentY, { align: 'center' });
      currentY += 8;
    });
    
    this.pageManager.setCurrentY(this.pageManager.getCurrentY() + 95);
  }

  private addScoreHighlight(results: TestResults): void {
    const pageWidth = this.pageManager.getPageWidth();
    const contentWidth = this.pageManager.getContentWidth();
    
    // Score highlight box
    const scoreColor = getStatusColor(results.overallStatus);
    this.pdf.setFillColor(...scoreColor);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      contentWidth,
      60,
      'F'
    );
    
    let currentY = this.pageManager.getCurrentY() + 20;
    
    // Overall score
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.title);
    this.pdf.setTextColor(...PDF_COLORS.text.white);
    this.pdf.text(
      `${Math.round(results.overallPercentage)}%`,
      pageWidth / 2,
      currentY,
      { align: 'center' }
    );
    
    currentY += 20;
    
    // Status
    this.pdf.setFontSize(PDF_FONTS.size.heading);
    this.pdf.text(
      results.overallStatus.toUpperCase(),
      pageWidth / 2,
      currentY,
      { align: 'center' }
    );
    
    this.pageManager.setCurrentY(this.pageManager.getCurrentY() + 75);
  }

  private addAssessmentSummary(results: TestResults): void {
    const pageWidth = this.pageManager.getPageWidth();
    
    this.pageManager.addSection();
    
    // Assessment details
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.text.primary);
    this.pdf.text(
      'Assessment Summary',
      pageWidth / 2,
      this.pageManager.getCurrentY(),
      { align: 'center' }
    );
    
    this.pageManager.addParagraph();
    
    const summaryItems = [
      `Completion Date: ${new Date(results.completedAt).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`,
      `Categories Evaluated: ${results.categories.length}`,
      `Total Questions: ${results.categories.length * 20}`,
      `Assessment Duration: Approximately 60 minutes`,
      `Ready Categories: ${results.categories.filter(c => c.status === 'Ready').length}`,
      `Almost Ready Categories: ${results.categories.filter(c => c.status === 'Almost Ready').length}`,
      `Not Ready Categories: ${results.categories.filter(c => c.status === 'Not Ready').length}`
    ];
    
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    
    summaryItems.forEach(item => {
      this.pdf.text(
        item,
        pageWidth / 2,
        this.pageManager.getCurrentY(),
        { align: 'center' }
      );
      this.pageManager.addLine();
    });
  }

  private addDisclaimer(): void {
    const pageWidth = this.pageManager.getPageWidth();
    const contentWidth = this.pageManager.getContentWidth();
    
    this.pageManager.setCurrentY(this.pageManager.getPageHeight() - 80);
    
    // Disclaimer box
    this.pdf.setFillColor(...PDF_COLORS.background.light);
    this.pdf.setDrawColor(...PDF_COLORS.text.light);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      contentWidth,
      40,
      'FD'
    );
    
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.small);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    
    const disclaimer = [
      'This assessment provides guidance based on your responses and should be used as a starting point',
      'for export planning. For specific advice, consult with export professionals and legal advisors.',
      'Lagos School of Export Management provides this tool for educational and planning purposes.'
    ];
    
    let currentY = this.pageManager.getCurrentY() + 10;
    disclaimer.forEach(line => {
      this.pdf.text(line, pageWidth / 2, currentY, { align: 'center' });
      currentY += 8;
    });
  }
}